import { useState, useEffect, useRef } from "react";
import { DomEvent } from "leaflet";
import axios from "axios";
import CloseButton from "../buttons/CloseButton";
import classes from "./ArmyForm.module.scss";

const ArmyForm = ({ onClose, army, nodeNumber, disabled }) => {
  const [name, setName] = useState(army ? army.name : "");
  const [general, setGeneral] = useState(army ? army.general : "");
  const [soldiers, setSoldiers] = useState(
    army ? army.soldiers.join("\n") : ""
  );
  const [walls, setWalls] = useState(army ? army.walls : false);
  const [ship, setShip] = useState(army ? army.ship : false);
  const [recruitment, setRecruitment] = useState(army ? army.recruitment : false);
  const [faction, setFaction] = useState(army ? army.faction : "none");
  const [country, setCountry] = useState(army ? army.country : "none");
  const [status, setStatus] = useState(army ? army.status : "enemy");
  const [node, setNode] = useState(army ? army.node : nodeNumber || 1);
  const [unsure, setUnsure] = useState(army ? army.unsure : false);
  const [comment, setComment] = useState(army ? army.comment : "");
  const apiUrl = process.env.REACT_APP_API_URL || '/api';
  let updatedDate = army
    ? new Date(army.updated_date).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    })
    : null;

  const token = window.localStorage.getItem("loggedBLUser");

  const containerRef = useRef(null);
  useEffect(() => {
    if (containerRef.current) {
      DomEvent.disableScrollPropagation(containerRef.current);
    }
  }, [containerRef]);

  const reload = () => {
    window.location.reload(false);
  };

  function handleAuthError(e) {
    if (e.response?.status === 401) {
      window.localStorage.removeItem("loggedBLUser");
      window.location.reload();
    } else {
      throw e;
    }
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    const soldiersArray = soldiers
      .split("\n")
      .map(function (soldier) {
        return soldier.trim();
      })
      .filter(function (soldier) {
        return soldier !== "";
      });

    const headers = { Authorization: `Bearer ${token}` };
    try {
      if (army) {
        await axios.put(`${apiUrl}/armies/${army.id}`,
          {
            name,
            general,
            soldiers: soldiersArray,
            faction,
            country,
            status,
            walls,
            ship,
            recruitment,
            node,
            unsure,
            comment,
          },
          { headers }
        );
      } else {
        await axios.post(`${apiUrl}/armies`,
          {
            name,
            general,
            soldiers: soldiersArray,
            faction,
            country,
            status,
            node,
            unsure,
            comment,
          },
          { headers }
        );
      }
      reload();
    } catch (e) {
      handleAuthError(e);
    }
  };

  const handleDelete = () => {
    axios.delete(`${apiUrl}/armies/${army.id}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    )
      .then(() => {
        reload();
      })
      .catch((e) => {
        handleAuthError(e);
      });
  };

  return (
    <div className={classes.form} ref={containerRef}>
      <CloseButton onClose={onClose} />

      <h5>{army ? "Edit Army" : "Add Army"}</h5>
      <div>
        {army && army.added_by && <p>Added by: {army.added_by.username}</p>}
        {updatedDate && <p>Last updated: {updatedDate}</p>}
      </div>
      <form onSubmit={handleSubmit}>
        <div className={classes.group}>
          <label>Name*:</label>
          <input
            type="text"
            placeholder="Name"
            defaultValue={name}
            onChange={(e) => setName(e.target.value)}
            disabled={disabled}
          />
        </div>
        <div className={classes.group}>
          <label>General*:</label>
          <input
            type="text"
            placeholder="General"
            defaultValue={general}
            onChange={(e) => setGeneral(e.target.value)}
            disabled={disabled}
          />
        </div>
        <div className={classes.group}>
          <label>Soldiers:</label>
          <textarea
            type="textarea"
            placeholder={"Soldier 1\nSoldier 2\nSoldier 3"}
            defaultValue={soldiers}
            onChange={(e) => setSoldiers(e.target.value)}
            disabled={disabled}
          />
        </div>
        <div className={classes.group}>
          <label>Country*:</label>
          <select
            defaultValue={country}
            onChange={(e) => setCountry(e.target.value)}
            disabled={disabled}
          >
            <option value="none">None</option>
            <option value="aegis">Aegis</option>
            <option value="albania">Albania</option>
            <option value="bosna">Bosna</option>
            <option value="bulgaria">Bulgaria</option>
            <option value="bursa">Bursa</option>
            <option value="capua">Capua</option>
            <option value="castilla">Castilla</option>
            <option value="cehz">Cehz</option>
            <option value="church">Church</option>
            <option value="coimbra">Coimbra</option>
            <option value="edirne">Edirne</option>
            <option value="estergom">Estergom</option>
            <option value="florentina">Florentina</option>
            <option value="genova">Genova</option>
            <option value="greece">Greece</option>
            <option value="imperium-legionis">Imperium Legionis</option>
            <option value="karesi">Karesi</option>
            <option value="l'aquila">L'Aquila</option>
            <option value="lisboa">Lisboa</option>
            <option value="milano">Milano</option>
            <option value="modena">Modena</option>
            <option value="poland-krakowskie">Poland-Krakowskie</option>
            <option value="poland-mazowieckie">Poland-Mazowieckie</option>
            <option value="poland-welikoplskie">Poland-Welikoplskie</option>
            <option value="porto">Porto</option>
            <option value="savoie">Savoie</option>
            <option value="serbia">Serbia</option>
            <option value="venetia">Venetia</option>
            <option value="wallachia">Wallachia</option>
            <option value="wien">Wien</option>
          </select>
        </div>
        <div className={classes.group}>
          <label>Faction*:</label>
          <select
            defaultValue={faction}
            onChange={(e) => setFaction(e.target.value)}
            disabled={disabled}
          >
            <option value="none">None</option>
            <option value="aegis">Aegis</option>
            <option value="albania">Albania</option>
            <option value="altay">Altay Ailesi</option>
            <option value="vidin">Baba Vida</option>
            <option value="bl-1">Battle Legion</option>
            <option value="bosna">Bosna</option>
            <option value="bulgaria">Bulgaria</option>
            <option value="bunny-legion">Bunny Legion</option>
            <option value="bursa">Bursa</option>
            <option value="capua">Capua</option>
            <option value="csb">Carthage Sacred Band</option>
            <option value="castilla">Castilla</option>
            <option value="cehz">Cehz</option>
            <option value="coimbra">Coimbra</option>
            <option value="edirne">Edirne</option>
            <option value="esercito-mercenario-dei-lanzichenecchi">Esercito Mercenario dei Lanzichenecchi</option>
            <option value="estergom">Estergom</option>
            <option value="fellowship">Fellowship</option>
            <option value="florentina">Florentina</option>
            <option value="bl-3">Foreign Legion</option>
            <option value="genova">Genova</option>
            <option value="golemi">Golemi</option>
            <option value="greece">Greece</option>
            <option value="haiduks">Haiduk Batalion</option>
            <option value="imperium-legionis">Imperium Legionis</option>
            <option value="karesi">Karesi</option>
            <option value="l'aquila">L'Aquila</option>
            <option value="lisboa">Lisboa</option>
            <option value="chaos">Lords of Chaos</option>
            <option value="manonera">ManoNera</option>
            <option value="medici">Medici</option>
            <option value="milano">Milano</option>
            <option value="modena">Modena</option>
            <option value="o.n.e">O.N.E</option>
            <option value="order-of-the-phoenix">Order of the Phoenix</option>
            <option value="oriente">Oriente</option>
            <option value="poland-krakowskie">Poland-Krakowskie</option>
            <option value="poland-mazowieckie">Poland-Mazowieckie</option>
            <option value="poland-welikoplskie">Poland-Welikoplskie</option>
            <option value="porto">Porto</option>
            <option value="ron">Riders of the Night</option>
            <option value="saruhan">Saruhanlı Ailesi</option>
            <option value="savoie">Savoie</option>
            <option value="scout">Scout</option>
            <option value="bl-2">Second Legion</option>
            <option value="seljuk">Selçuklu Ailesi</option>
            <option value="serbia">Serbia</option>
            <option value="sl">Steel Legion</option>
            <option value="tau-rohen">Tau/Rohen</option>
            <option value="venetia">Venetia</option>
            <option value="wallachia">Wallachia</option>
            <option value="wien">Wien</option>
          </select>
        </div>
        <div className={classes.group}>
          <label>Status*:</label>
          <select
            defaultValue={status}
            onChange={(e) => setStatus(e.target.value)}
            disabled={disabled}
          >
            <option value={"friend"}>Friend</option>
            <option value="neutral">Neutral</option>
            <option value="enemy">Enemy</option>
            <option value="scout">Scout</option>
          </select>
        </div>
        {army && (
          <div className={classes.group}>
            <label>Behind Walls:</label>
            <select
              defaultValue={walls ? "true" : "false"}
              onChange={(e) => setWalls(e.target.value === "true")}
              disabled={disabled}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        )}
        {army && (
          <div className={classes.group}>
            <label>On Ship:</label>
            <select
              defaultValue={ship ? "true" : "false"}
              onChange={(e) => setShip(e.target.value === "true")}
              disabled={disabled}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        )}
        {army && (
          <div className={classes.group}>
            <label>Recruitment:</label>
            <select
              defaultValue={recruitment ? "true" : "false"}
              onChange={(e) => setRecruitment(e.target.value === "true")}
              disabled={disabled}
            >
              <option value="true">Yes</option>
              <option value="false">No</option>
            </select>
          </div>
        )}
        <div className={classes.group}>
          <label>Node*:</label>
          <input
            placeholder="Node"
            defaultValue={node}
            onChange={(e) => setNode(parseInt(e.target.value))}
            onWheel={(e) => e.target.blur()}
            disabled={disabled}
          />
        </div>
        <div className={classes.group}>
          <label>Unsure*:</label>
          <select
            defaultValue={unsure ? "true" : "false"}
            onChange={(e) => setUnsure(e.target.value === "true")}
            disabled={disabled}
          >
            <option value="false">No</option>
            <option value="true">Yes</option>
          </select>
        </div>
        <div className={classes.group}>
          <label>Notes:</label>
          <textarea
            type="textarea"
            defaultValue={comment}
            onChange={(e) => setComment(e.target.value)}
            disabled={disabled}
          />
        </div>
        {!disabled && (
          <input type="submit" value="Submit" className={classes.btn} />
        )}
      </form>
      {!disabled && army && (
        <button
          onClick={() => {
            handleDelete();
          }}
          className={`${classes.btn} ${classes.delete}`}
        >
          Delete
        </button>
      )}
    </div>
  );
};

export default ArmyForm;
